import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { formatDate, sortTable } from '../../../../../../../common/SharedFunctions';
import { BrokerClientApi } from '@apply/api';
import { applicationInsights } from '../../../../../../../ApplicationInsights';
import { RefreshEvents } from '../../../../../../../common/SharedEvents';
import {
    Card,
    CardHeaderTheme,
    CardBorderRadius,
    CardBorderStyle,
    CardTheme,
    Table,
    TableHeaderTheme,
    TableBorderRadius,
    TableBorderStyle,
    TableRowTheme,
    TableColumn,
    TableRow,
} from '@apply/components';
import { RepresentativeCell } from './components/RepresentativeCell';
import { isMobileScreen } from '@apply/ui';
import { BusinessNameCell } from './components/BusinessNameCell';
import { ReferralTableLoadIndicator } from './components/ReferralTableLoadIndicator';
import { ICCAStartDateCell } from './components/ICCAStartDateCell';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { TableFilteringOperators } from './constants/TableFilteringOperators';
import { SortState } from './constants/TableSortingOperators';
import { EmptyReferrals } from './components/EmptyReferrals';

interface ReferralRow {
    personName?: string;
    businessName?: string;
    firstName?: string;
    lastName?: string;
    startDate?: string;
}

interface PendingReferralTableProps {
    searchText: string;
}

export const PendingReferralTable: React.FC<PendingReferralTableProps> = ({ searchText }) => {
    const [rows, set_rows] = useState([]);
    const isMobile = isMobileScreen();
    const brokerClientApi = new BrokerClientApi.BrokerClientApi(applicationInsights);
    const refreshEvents = new RefreshEvents();
    const filteringOperators = new TableFilteringOperators(searchText);
    const [isLoading, set_isLoading] = useState(false);

    const [sort] = useState<SortState>({
        column: 'personName',
        direction: 'asc',
    });
    const filtered = filteringOperators.filterRows(rows);
    const mapped: ReferralRow[] = filtered.map((row) => ({
        ...row,
        startDate: formatDate(new Date(row.iccaDate)),
    }));
    const sortedRows = _.orderBy(mapped, sortTable(sort), sort.direction);

    const _loadReferralClients = async () => {
        set_isLoading(true);
        const response = await brokerClientApi.getPendingReferralsGridRows();
        set_isLoading(false);

        if (response.statusCode !== 200) return;

        set_rows(response.result);
    };

    useEffect(() => {
        async function load() {
            await _loadReferralClients();

            refreshEvents.addRefreshListener(_loadReferralClients);
        }

        load();
    }, []);

    const renderReferralRepresentative = (representative: string | undefined) => {
        return <RepresentativeCell representative={representative} />;
    };

    const renderReferralBusinessName = (businessName: string | undefined) => {
        return <BusinessNameCell businessName={businessName} />;
    };

    const renderReferralICCAStartDate = (iccaDate: string | undefined) => {
        return <ICCAStartDateCell iccaStart={iccaDate} />;
    };

    const isEmptyResults = sortedRows.length === 0;
    const data: TableRow[] =
        sortedRows.length === 0
            ? [
                  {
                      personName: renderReferralRepresentative(undefined),
                      businessName: renderReferralBusinessName(undefined),
                      startDate: renderReferralICCAStartDate(undefined),
                  },
              ]
            : sortedRows.map((referral) => ({
                  personName: renderReferralRepresentative(referral.personName),
                  businessName: renderReferralBusinessName(referral.businessName),
                  startDate: renderReferralICCAStartDate(referral.startDate),
              }));

    const cardHeader = () => {
        return (
            <>
                <div>
                    <FontAwesomeIcon
                        icon={faPaperPlane}
                        className='icon'
                        style={{ paddingRight: '10px', opacity: '60%' }}
                    />
                    {`Pending Referrals ${!isMobile ? '(waiting on BFI)' : ''}`}
                </div>
            </>
        );
    };

    return (
        <>
            <Card
                header={cardHeader()}
                headerTheme={CardHeaderTheme.Gray}
                theme={CardTheme.White}
                borderRadius={CardBorderRadius.Small}
                borderStyle={CardBorderStyle.Solid}
                collapsible={true}
            >
                {isLoading ? (
                    <ReferralTableLoadIndicator message='Loading your Pending Referrals...' />
                ) : !isEmptyResults ? (
                    <Table
                        headerTheme={TableHeaderTheme.Transparent}
                        rowTheme={TableRowTheme.Transparent}
                        borderRadius={TableBorderRadius.None}
                        borderStyle={TableBorderStyle.None}
                        showHeader={true}
                        data={data}
                        width='100%'
                    >
                        <TableColumn
                            columnKey='personName'
                            header={'Representative'}
                            width='30%'
                        />
                        <TableColumn
                            columnKey='businessName'
                            header='Business'
                            width={'50%'}
                        />
                        <TableColumn
                            columnKey='startDate'
                            header='ICCA Start'
                            width='20%'
                        />
                    </Table>
                ) : (
                    <EmptyReferrals />
                )}
            </Card>
        </>
    );
};
